import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '#components/atoms/tooltip';
import clsx from 'clsx';
import {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  forwardRef,
  ReactNode,
} from 'react';
import './Button.scss';

type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'secondary-neutral'
  | 'tertiary'
  | 'tertiary-neutral'
  | 'danger'
  | 'icon'
  | 'icon-neutral';

type IconPosition = 'left' | 'right' | 'none';

export interface TdsButtonBaseProps {
  variant?: ButtonVariant;
  icon?: ReactNode;
  iconPosition?: IconPosition;
  isLoading?: boolean;
  tooltip?: string;
  className?: string;
  disabled?: boolean;
  compact?: boolean;
}

interface ButtonElementProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    TdsButtonBaseProps {
  href?: never;
  light?: boolean;
  renderLink?: never;
}

interface AnchorElementProps
  extends AnchorHTMLAttributes<HTMLAnchorElement>,
    TdsButtonBaseProps {
  href: string;
  renderLink?: (
    props: Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'children'> & {
      children: ReactNode;
      href: string;
    },
  ) => ReactNode;
  light?: boolean;
}

type ButtonProps = ButtonElementProps | AnchorElementProps;

export const Button = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonProps
>(
  (
    {
      variant = 'primary',
      icon,
      iconPosition = 'none',
      isLoading = false,
      href,
      tooltip,
      children,
      className,
      disabled,
      light,
      compact = false,
      renderLink,
      ...props
    },
    ref,
  ) => {
    const getAriaLabel = () => {
      if (isLoading) return 'Laadimine, palun oodake';
      if (variant.startsWith('icon')) return props['aria-label'] ?? '';
      return '';
    };

    const commonProps = {
      className: clsx(
        'tds-button',
        `tds-button--${variant}`,
        {
          'tds-button--light': light,
          'tds-button--compact': compact && !variant.startsWith('icon'),
          'tds-button--icon--compact': compact && variant.startsWith('icon'),
          [`tds-button--icon-${iconPosition}`]: iconPosition !== 'none',
          'is-disabled': disabled,
          'is-loading': isLoading,
        },
        className,
      ),
      'aria-label': getAriaLabel(),
      'aria-busy': isLoading,
      ref,
    };

    const finalProps = {
      ...(href && !disabled
        ? { href, ...(props as AnchorHTMLAttributes<HTMLAnchorElement>) }
        : {
            type:
              (props as ButtonHTMLAttributes<HTMLButtonElement>).type ??
              'button',
            disabled: disabled || isLoading,
            'aria-disabled': disabled || isLoading,
            ...(props as ButtonHTMLAttributes<HTMLButtonElement>),
          }),
      ...commonProps,
    };

    const renderLoadingSpinner = () => {
      if (!isLoading) return null;
      return (
        <span className="tds-button__spinner">
          <svg className="tds-spinner" viewBox="0 0 50 50">
            <circle
              className="tds-spinner__path"
              cx="25"
              cy="25"
              r="20"
              fill="none"
              strokeWidth="5"
            />
          </svg>
        </span>
      );
    };

    const renderIcon = () => {
      if (!icon) return null;
      return <span className="tds-icon">{icon}</span>;
    };

    const renderChildren = () => {
      if (variant.startsWith('icon'))
        return <span className="tds-icon">{children}</span>;
      return children;
    };

    const renderContent: ReactNode = (
      <>
        {isLoading && renderLoadingSpinner()}
        {!isLoading && iconPosition === 'left' && renderIcon()}
        {!isLoading && renderChildren()}
        {!isLoading && iconPosition === 'right' && renderIcon()}
      </>
    );

    if (href && renderLink) {
      return renderLink({
        ...(finalProps as AnchorHTMLAttributes<HTMLAnchorElement>),
        href,
        children: renderContent,
      });
    }

    let Comp;
    if (href && !disabled) {
      Comp = 'a';
    } else {
      Comp = 'button';
    }

    if (tooltip) {
      return (
        <Comp {...finalProps}>
          {renderContent}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="tooltip absolute h-full w-full" />
              </TooltipTrigger>
              <TooltipContent
                collisionPadding={{
                  top: 100,
                }}
              >
                {tooltip}
                <TooltipArrow />
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </Comp>
      );
    }

    return <Comp {...finalProps}>{renderContent}</Comp>;
  },
);

Button.displayName = 'Button';
